import Button, { ButtonTypes } from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import Loader from "atoms/loaders/loader";
import FileRecord from "models/view-models/file-record";
import ImageUploadField from "molecules/form-fields/image-upload-field";
import React, { useState } from "react";
import FileUploadDestination from "utilities/enumerations/file-upload-destination";
import MimeTypes from "utilities/enumerations/mime-type";
import useUserUploadId from "utilities/hooks/domain/image-upload/use-user-upload-id";
import { t } from "utilities/localization-utils";

const CSS_BASE_CLASS = "c-image-preview-field";

interface ImagePreviewFieldProps {
    image?: FileRecord;
    imageDestination: FileUploadDestination;
    onAdd: (fileToDelete: FileRecord) => void;
    onDelete: (fileToDelete: FileRecord) => void;
    onReplace?: (fileToReplace: FileRecord) => void;
    uploadText?: string;
}
const ImagePreviewField = (props: ImagePreviewFieldProps) => {
    const { image, onAdd, onDelete, onReplace, imageDestination, uploadText } =
        props;
    const [isLoading, setIsLoading] = useState(false);
    const { userUploadId } = useUserUploadId();
    const handleAddImage = (newImage?: FileRecord) => {
        newImage ? onAdd(newImage) : onDelete(image!);
        setIsLoading(false);
    };
    const handleDelete = () => {
        onDelete(image!);
    };
    const handleOnReplaceImage = () => {
        onReplace?.(image!);
    };
    return (
        <>
            {image ? (
                <div className={`${CSS_BASE_CLASS}__container`}>
                    <div className={`${CSS_BASE_CLASS}__container__left`}>
                        <img
                            data-test-id="image-preview-field--image"
                            className={`${CSS_BASE_CLASS}__display-image`}
                            alt={""}
                            src={image.presignedUrl}
                        />
                        <div className={`${CSS_BASE_CLASS}__body`}>
                            <span
                                data-test-id="image-preview-field--filename"
                                className={`${CSS_BASE_CLASS}__filename`}>
                                {image.fileName()}
                            </span>
                            <span className={`${CSS_BASE_CLASS}__size`}>
                                {image.size}
                            </span>
                        </div>
                    </div>
                    <div className={`${CSS_BASE_CLASS}__container__right`}>
                        <Button
                            dataTestId="image-preview-field--delete-button"
                            onClick={handleDelete}
                            style={ButtonStyles.None}
                            cssClassName={`${CSS_BASE_CLASS}__delete-button`}
                            type={ButtonTypes.Button}>
                            <span className="sr-only">{t("delete")}</span>
                            <Icon
                                cssClassName={`${CSS_BASE_CLASS}__delete-icon`}
                                type={Icons.Trashcan}
                                size={IconSizes.Small}
                            />
                        </Button>
                        {onReplace && (
                            <Button
                                dataTestId="image-preview-field--replace-button"
                                onClick={handleOnReplaceImage}
                                style={ButtonStyles.None}
                                cssClassName={`${CSS_BASE_CLASS}__replace-button`}
                                type={ButtonTypes.Button}>
                                <span className="sr-only">
                                    {t("editItem", { item: t("image") })}
                                </span>
                                <Icon
                                    cssClassName={`${CSS_BASE_CLASS}__replace-icon`}
                                    type={Icons.ImageReplace}
                                    size={IconSizes.Small}
                                />
                            </Button>
                        )}
                    </div>
                </div>
            ) : (
                <ImageUploadField
                    onUploadStart={() => setIsLoading(true)}
                    userUploadId={userUploadId}
                    value={image}
                    onFileChanged={handleAddImage}
                    accept={MimeTypes.Image}
                    fileUploadDestination={imageDestination}
                    uploadButtonText={
                        uploadText ?? t("addItem", { item: t("image") })
                    }
                />
            )}
            {isLoading && <Loader />}
        </>
    );
};

export default ImagePreviewField;
